import { useEffect, useMemo, useRef, type PropsWithChildren } from 'react';

import { highlight } from './FilterStatement.sty.js';
import type { StylingProps } from '../../../core/types/styling-props.js';


function getTextRangeForHighlight(element: HTMLElement, searchTerm: string[]) {
  const textNodes: Node[] = [];

  // Helper to collect all text nodes
  function collectTextNodes(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node);
    } else {
      for (const child of Array.from(node.childNodes)) {
        collectTextNodes(child);
      }
    }
  }

  collectTextNodes(element);

  const ranges: Array<Range> = [];
  for (const textNode of textNodes) {
    const textContent = textNode.textContent?.toLocaleLowerCase() ?? '';

    for (const searchTermPart of searchTerm) {
      let index = 0;

      while ((index = textContent.indexOf(searchTermPart, index)) !== -1) {
        const start = index;
        let end = index + searchTermPart.length;

        // Spaces after highlighted tokens are also highlighted.
        while (end < textContent.length && /\s/.test(textContent[end])) {
          end++;
        }

        const range = new Range();
        range.setStart(textNode, start);
        range.setEnd(textNode, Math.min(end, textContent.length));
        ranges.push(range);
        // Skip past the term + any whitespace we just consumed.
        index = end;
      }
    }
  }
  if (ranges.length > 0) {
    return ranges;
  }

  return null; // No match found
}

const registeredHighlights = new Map<string, Set<AbstractRange> | Highlight>();

/**
 * Helper function to either create or receive a registered Highlight.
 */
export function getOrCreateNamedHighlight(
  highlightName: string,
): Set<AbstractRange> | Highlight {
  const storedHighlight = registeredHighlights.get(highlightName);
  if (storedHighlight) {
    return storedHighlight;
  }
  let createdHighlight: Set<AbstractRange> | Highlight = new Set();
  if ('Highlight' in window) {
    createdHighlight = new Highlight();
    if (createdHighlight instanceof Highlight) {
      CSS.highlights.set(highlightName, createdHighlight);
    }
  }
  registeredHighlights.set(highlightName, createdHighlight);
  return createdHighlight;
}

/**
 * Creates a local range array that has been added to the highlights and returns it.
 * @internal
 */
export function createRangedHighlights(
  targetElements: Array<HTMLElement>,
  term: string,
  highlight: Set<AbstractRange> | Highlight,
): Array<Range> {
  const splitTerm = term
    .toLocaleLowerCase()
    .split(/([(),"])|\s/g) // splits by whitespace, parentheses, and commas
    .filter(Boolean);

  const storedRanges: Array<Range> = [];

  for (const target of Array.from(targetElements)) {
    const ranges = getTextRangeForHighlight(target, splitTerm);
    if (ranges === null) {
      continue;
    }

    for (const range of ranges) {
      try {
        highlight.add(range);
        storedRanges.push(range);
      } catch {
        //
      }
    }
  }

  return storedRanges;
}

/**
 * @internal
 */
export function CSSHighlight(
  props: Readonly<
    PropsWithChildren<
      { highlightName: string; term: string; selector: string } & StylingProps
    >
  >,
) {
  const { children, highlightName, term, selector, className } = props;
  const rootRef = useRef<HTMLSpanElement>(null);

  const suggestionHighlight = useMemo(
    () => getOrCreateNamedHighlight(highlightName),
    [highlightName],
  );

  useEffect(() => {
    const currentElement = rootRef.current;

    const storedRanges: Array<Range> = [];
    const highlightedParents: Set<Element> = new Set();

    if (currentElement) {
      const targetElements =
        currentElement.querySelectorAll<HTMLElement>(selector) ?? [];

      // Do the following steps one by one as doing it all in a spread push to the
      // storedRanges might lead to a callstack size exceeded error.
      const elements = Array.from(targetElements);
      const rangedHighlights = createRangedHighlights(
        elements,
        term,
        suggestionHighlight,
      );

      for (const highlight of rangedHighlights) {
        storedRanges.push(highlight);
      }

      // Add class to parent elements of highlighted ranges
      for (const range of storedRanges) {
        const parent = range.startContainer.parentElement;
        if (parent) {
          parent.classList.add(highlight);
          highlightedParents.add(parent);
        }
      }
    }

    return () => {
      for (const parent of highlightedParents) {
        parent.classList.remove(highlight);
      }
      for (const storedRange of storedRanges) {
        try {
          suggestionHighlight.delete(storedRange);
        } catch {
          //
        }
      }
    };
  }, [children, selector, suggestionHighlight, term]);

  return (
    <span ref={rootRef} className={className} style={{ display: 'contents' }}>
      {children}
    </span>
  );
}
